/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import PatientProfile from './components/PatientProfile'
import PatientAppointments from './components/PatientAppointments'
import OrganData from './components/OrganData'
import { togglePopup } from '../../../Redux/popups/popupSlice'
import { useTranslation } from 'react-i18next'
import printPatientPDF from '../../../utils/printPatientPDF'
import logoText from '../../../assets/images/logo.png'
import { getAllUsers } from '../../../Redux/users/usersSlice'

const PatientPage = () => {
  const param = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation() || ((i) => i)
  const { patientId } = param
  const auth = useSelector((state) => state.auth)
  const { profile, user } = auth.user || {}
  const usedName = 'Dr. ' + (profile?.displayName || user?.displayName || 'No name')
  const signature = profile?.signature || ''

  const { patients } = useSelector((state) => state.patients)
  const aptData = useSelector((state) => state.appointments?.appointments)
  const optData = useSelector((state) => state.operations?.operations)
  const { users } = useSelector((state) => state.users)
  const [currentPatient, setCurrentPatient] = useState(null)
  const [checkedPatient, setCheckedPatient] = useState(false)

  useEffect(() => {
    setCurrentPatient(patients?.find((pt) => pt.id === patientId));
    setCheckedPatient(true);
  }, [patients, patientId]);

  // Determine if the patient is from stomatology category
  const isStomatologyPatient = currentPatient?.category === 'stomatology' || window.location.pathname?.includes('stomatology')

  const navigateBack = () => {
    const currentCategory = window.location.pathname?.includes('stomatology') ? 'stomatology' : 'pulmonology'
    navigate(`/management/${currentCategory}/`)
  }

  // Only navigate back if we've checked for the patient and it's not found
  useEffect(() => {
    if (checkedPatient && (!patientId || !currentPatient?.id)) {
      navigateBack();
    }
  }, [checkedPatient, patientId, currentPatient]);

  const fullDataPatient = {
    ...currentPatient,
    operations: currentPatient?.operations?.map((opt) => {
      const found = optData.find((op) => op.id === opt.operation_id) || {}
      
      // Find the appointment for this operation instance (by init_date)
      const opAppointment = aptData.find(app =>
        app.patient_id === currentPatient?.id &&
        app.operation_id === opt.operation_id &&
        app.date === opt.init_date // match by operation_id and init_date
      );
      
      // Use the custom name only for this operation instance
      const customServiceName = opAppointment && opAppointment.name && opAppointment.name !== opAppointment.operation_name
        ? opAppointment.name
        : null;
      
      return {
        ...opt,
        ...found,
        custom_service_name: customServiceName
      }
    }),
    appointments: aptData.filter((app) => app.patient_id === currentPatient?.id)
  }

  // Initialize currentOperation as null instead of empty object
  const [currentOperation, setCurrentOperation] = useState(null)
  const [showGroupedTeeth, setShowGroupedTeeth] = useState(null)

  useEffect(() => {
    if (fullDataPatient) {
      // If we have a currentOperation, try to find and update it
      if (currentOperation) {
        const found = fullDataPatient?.operations?.find((op) => op.init_date === currentOperation.init_date)
        if (found) {
          setCurrentOperation(found)
        }
      }
    } else {
      navigateBack()
    }
  }, [currentPatient])

  useEffect(() => {
    if (!users?.length) {
      dispatch(getAllUsers())
    }
  }, [users, dispatch])

  const { appointments } = fullDataPatient

  const handleEdit = () => {
    dispatch(
      togglePopup({
        pointer: 4,
        data: {
          ...currentPatient
        }
      })
    )
  }

  const addAptWithCurrentOp = () => {
    if (!currentOperation?.init_date) {
      alert('Please select an operation first')
    }
    const payload = {
      patientId,
      operationId: currentOperation?.operation_id,
      init_date: currentOperation?.init_date,
      is_ongoing: true
    }
    dispatch(
      togglePopup({
        pointer: 5,
        data: payload
      })
    )
  }

  const toggleAttachmentPopup = () => {
    dispatch(
      togglePopup({
        pointer: 6,
        data: {
          patientId,
          attachements: fullDataPatient?.attachements,
          operations: fullDataPatient?.operations,
          selectedOpId: currentOperation?.init_date,
          customStyle: {
            maxWidth: '800px'
          }
        }
      })
    )
  }

  const printrecord = (operationArg) => {
    const op = operationArg || currentOperation || {};
    // Get the doctor's data from the users list
    const doctor = users?.find(user => user.id === op.doctor_id);
    
    // For pulmonology patients without a selected service, use Vera Lleshi
    const isPulmonologyWithoutService = currentPatient?.category === 'pulmonology' && !op.doctor_id;
    const veraLleshi = users?.find(user => user.displayName === 'Vera Lleshi');
    
    printPatientPDF({
      patientData: {
        ...fullDataPatient,
        rtg_pa_pulmo: fullDataPatient?.rtg_pa_pulmo || '',
        ekg: fullDataPatient?.ekg || '',
        ta: fullDataPatient?.ta || '',
        spo2: fullDataPatient?.spo2 || '',
        dg: fullDataPatient?.dg || '',
        main_issue: fullDataPatient?.main_issue || ''
      },
      operation: {
        ...op,
        doctor_signature: isPulmonologyWithoutService ? (veraLleshi?.signature || '') : (doctor?.signature || ''),
        doctor_name: isPulmonologyWithoutService ? 'Dr. Vera Lleshi' : (doctor?.displayName || op.doctor_name),
        main_issue: op?.main_issue || fullDataPatient?.main_issue || op?.description || '',
        custom_service_name: op?.custom_service_name || op?.name,
        op_name: op?.op_name || op?.operation_name,
        init_date: op?.init_date || op?.date,
        feedback: op?.feedback,
        examination: op?.examination,
        therapy: op?.therapy,
        xray: fullDataPatient?.rtg_pa_pulmo || '',
        ekg: fullDataPatient?.ekg || '',
        ta: fullDataPatient?.ta || '',
        spo: fullDataPatient?.spo2 || '',
        dg: fullDataPatient?.dg || ''
      },
      doctorName: isPulmonologyWithoutService ? 'Dr. Vera Lleshi' : usedName,
      signature: isPulmonologyWithoutService ? (veraLleshi?.signature || '') : signature
    });
  }

  const deletePt = () => {
    dispatch(togglePopup({ pointer: 4, data: { ...currentPatient, deleteData: true } }))
  }

  return (
    <div className={styles.container}>
      <div className={styles.patient_profile}>
        <PatientProfile
          profileId={patientId}
          fullDataPatient={fullDataPatient}
          currentOperation={currentOperation}
          setCurrentOperation={setCurrentOperation}
          handleEdit={handleEdit}
          toggleAttachmentPopup={toggleAttachmentPopup}
          printrecord={printrecord}
          deletePt={deletePt}
          t={t}
        />
      </div>
      <div className={styles.patient_data}>
        <div className={styles.pt_appointments_container}>
          <PatientAppointments
            t={t}
            appointments={appointments}
            currentOperation={currentOperation}
            addAptWithCurrentOp={addAptWithCurrentOp}
            patient={fullDataPatient}
            setShowGroupedTeeth={setShowGroupedTeeth}
          />
        </div>
        {/* Always render OrganData, the SVGMouth part is conditionally rendered inside */}
        <div className={styles.pt_organe_data}>
          <OrganData 
            t={t} 
            patientId={patientId} 
            currentOperation={currentOperation} 
            allOperations={fullDataPatient.operations} 
            patientRoom={currentPatient?.room}
            setCurrentOperation={setCurrentOperation}
            showGroupedTeeth={showGroupedTeeth}
            setShowGroupedTeeth={setShowGroupedTeeth}
          />
        </div>
      </div>
    </div>
  )
}

export default PatientPage
